import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Search, Pencil, Power, RotateCcw } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import api from "@/lib/api";
import { money } from "@/lib/format";
import { getHomeCatalogCompleteness, hasHomePhoto, hasFloorplan } from "@/lib/homeCatalogCompleteness";
import { getApiErrorMessage } from "@/lib/apiError";

const EMPTY_HOME = {
    manufacturer: "",
    series: "",
    model_name: "",
    home_type: "",
    beds: "",
    baths: "",
    sqft: "",
    width: "",
    length: "",
    dimensions: "",
    wind_zone: "",
    est_factory_cost: "",
    msrp: "",
    ehs_price: "",
    short_description: "",
    image_url: "",
    floorplan_image_url: "",
    active: true,
};

function HomeForm({ value, onChange }) {
    const set = (k, v) => onChange({ ...value, [k]: v });
    const num = (k, v) => set(k, v === "" ? "" : Number(v));
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[70vh] overflow-y-auto pr-1">
            <div className="md:col-span-2">
                <Label>Manufacturer *</Label>
                <Input
                    value={value.manufacturer}
                    onChange={(e) => set("manufacturer", e.target.value)}
                    placeholder="e.g. Live Oak Homes"
                    data-testid="catalog-form-manufacturer-input"
                />
            </div>
            <div>
                <Label>Series</Label>
                <Input
                    value={value.series || ""}
                    onChange={(e) => set("series", e.target.value)}
                    placeholder="e.g. Sunview"
                    data-testid="catalog-form-series-input"
                />
            </div>
            <div>
                <Label>Model name *</Label>
                <Input
                    value={value.model_name}
                    onChange={(e) => set("model_name", e.target.value)}
                    placeholder="e.g. Sunview 3BR"
                    data-testid="catalog-form-model-input"
                />
            </div>
            <div>
                <Label>Type</Label>
                <Select value={value.home_type || ""} onValueChange={(v) => set("home_type", v)}>
                    <SelectTrigger data-testid="catalog-form-type-select">
                        <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="Single-wide">Single-wide</SelectItem>
                        <SelectItem value="Double-wide">Double-wide</SelectItem>
                        <SelectItem value="Triple-wide">Triple-wide</SelectItem>
                        <SelectItem value="Modular">Modular</SelectItem>
                    </SelectContent>
                </Select>
            </div>
            <div>
                <Label>Wind zone</Label>
                <Select value={value.wind_zone || ""} onValueChange={(v) => set("wind_zone", v)}>
                    <SelectTrigger data-testid="catalog-form-windzone-select">
                        <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="I">I</SelectItem>
                        <SelectItem value="II">II</SelectItem>
                        <SelectItem value="III">III</SelectItem>
                    </SelectContent>
                </Select>
            </div>
            <div>
                <Label>Bedrooms</Label>
                <Input
                    type="number"
                    value={value.beds || ""}
                    onChange={(e) => num("beds", e.target.value)}
                    data-testid="catalog-form-beds-input"
                />
            </div>
            <div>
                <Label>Bathrooms</Label>
                <Input
                    type="number"
                    step="0.5"
                    value={value.baths || ""}
                    onChange={(e) => num("baths", e.target.value)}
                    data-testid="catalog-form-baths-input"
                />
            </div>
            <div>
                <Label>Square feet</Label>
                <Input
                    type="number"
                    value={value.sqft || ""}
                    onChange={(e) => num("sqft", e.target.value)}
                    data-testid="catalog-form-sqft-input"
                />
            </div>
            <div>
                <Label>Dimensions</Label>
                <Input
                    value={value.dimensions || ""}
                    onChange={(e) => set("dimensions", e.target.value)}
                    placeholder="e.g. 28x52"
                    data-testid="catalog-form-dimensions-input"
                />
            </div>
            <div>
                <Label>EHS price (base)</Label>
                <Input
                    type="number"
                    value={value.ehs_price || ""}
                    onChange={(e) => num("ehs_price", e.target.value)}
                    placeholder="0"
                    data-testid="catalog-form-ehs-price-input"
                />
            </div>
            <div>
                <Label>Estimated factory cost (internal)</Label>
                <Input
                    type="number"
                    value={value.est_factory_cost || ""}
                    onChange={(e) => num("est_factory_cost", e.target.value)}
                    placeholder="0"
                    data-testid="catalog-form-factory-cost-input"
                />
            </div>

            <div className="md:col-span-2 pt-2 border-t border-slate-200">
                <div className="text-sm font-semibold text-slate-900">Quote packet media</div>
                <div className="text-xs text-slate-500">
                    Stored URLs are copied into new quote snapshots and used by the quote PDF.
                    Existing alternate fields (photo_url, primary_image_url, exterior_image_url, floorplan_url, floor_plan_url, plan_image_url) remain supported.
                </div>
            </div>
            <div className="md:col-span-2">
                <Label>Short description</Label>
                <Input
                    value={value.short_description || ""}
                    onChange={(e) => set("short_description", e.target.value)}
                    placeholder="Brief customer-facing home summary"
                    data-testid="catalog-form-short-description-input"
                />
            </div>
            <div>
                <Label>Home photo URL</Label>
                <Input
                    value={value.image_url || ""}
                    onChange={(e) => set("image_url", e.target.value)}
                    placeholder="https://..."
                    data-testid="catalog-form-home-image-url-input"
                />
            </div>
            <div>
                <Label>Floorplan image URL</Label>
                <Input
                    value={value.floorplan_image_url || ""}
                    onChange={(e) => set("floorplan_image_url", e.target.value)}
                    placeholder="https://..."
                    data-testid="catalog-form-floorplan-image-url-input"
                />
            </div>

            <div className="md:col-span-2 flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2">
                <div>
                    <div className="text-sm font-medium">Active</div>
                    <div className="text-xs text-slate-500">
                        Inactive homes are hidden from the Quote Builder catalog.
                    </div>
                </div>
                <Switch
                    checked={!!value.active}
                    onCheckedChange={(v) => set("active", v)}
                    data-testid="catalog-form-active-switch"
                />
            </div>
        </div>
    );
}

export function CatalogSection() {
    const [homes, setHomes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [manufacturerFilter, setManufacturerFilter] = useState("__all__");
    const [showInactive, setShowInactive] = useState(true);

    const [openDialog, setOpenDialog] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY_HOME);

    const load = async () => {
        setLoading(true);
        try {
            const params = { include_inactive: true, limit: 1000 };
            const r = await api.get("/homes", { params });
            setHomes(r.data);
        } catch (err) {
            toast.error("Failed to load catalog");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const manufacturers = useMemo(() => {
        const set = new Set(homes.map((h) => h.manufacturer).filter(Boolean));
        return Array.from(set).sort();
    }, [homes]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return homes.filter((h) => {
            if (!showInactive && !h.active) return false;
            if (manufacturerFilter !== "__all__" && h.manufacturer !== manufacturerFilter) return false;
            if (!q) return true;
            return (
                (h.manufacturer || "").toLowerCase().includes(q) ||
                (h.series || "").toLowerCase().includes(q) ||
                (h.model_name || "").toLowerCase().includes(q)
            );
        });
    }, [homes, search, manufacturerFilter, showInactive]);

    const counts = useMemo(() => {
        const total = homes.length;
        const active = homes.filter((h) => h.active).length;
        return { total, active, inactive: total - active };
    }, [homes]);

    const startCreate = () => {
        setEditing(null);
        setForm(EMPTY_HOME);
        setOpenDialog(true);
    };

    const startEdit = (home) => {
        setEditing(home);
        setForm({ ...EMPTY_HOME, ...home });
        setOpenDialog(true);
    };

    const submit = async () => {
        const payload = { ...form };
        ["beds", "baths", "sqft", "width", "length", "ehs_price", "est_factory_cost", "msrp"].forEach((k) => {
            if (payload[k] === "" || payload[k] === null || payload[k] === undefined) delete payload[k];
        });
        ["short_description", "image_url", "floorplan_image_url"].forEach((k) => {
            if (payload[k] === "") delete payload[k];
        });
        if (!payload.manufacturer || !payload.model_name) {
            toast.error("Manufacturer and model name are required");
            return;
        }
        try {
            if (editing) {
                await api.patch(`/homes/${editing.id}`, payload);
                toast.success("Home updated");
            } else {
                await api.post("/homes", payload);
                toast.success("Home added to catalog");
            }
            setOpenDialog(false);
            await load();
        } catch (err) {
            toast.error(getApiErrorMessage(err, "Save failed"));
        }
    };

    const toggleActive = async (home) => {
        try {
            await api.patch(`/homes/${home.id}`, { active: !home.active });
            toast.success(!home.active ? "Home reactivated" : "Home deactivated");
            await load();
        } catch (err) {
            toast.error(getApiErrorMessage(err, "Update failed"));
        }
    };

    return (
        <Card className="rounded-xl border-slate-200 p-5 space-y-4" data-testid="catalog-section">
            <div className="flex flex-wrap items-end gap-3 justify-between">
                <div>
                    <div className="text-sm font-semibold text-slate-900">Home catalog</div>
                    <div className="text-xs text-slate-500">
                        {counts.active} active · {counts.inactive} inactive · {counts.total} total
                    </div>
                </div>
                <Dialog open={openDialog} onOpenChange={setOpenDialog}>
                    <DialogTrigger asChild>
                        <Button
                            className="bg-[#0F2A47] hover:bg-[#14385E] text-white"
                            onClick={startCreate}
                            data-testid="catalog-add-home-button"
                        >
                            <Plus className="mr-2 h-4 w-4" /> Add home
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl">
                        <DialogHeader>
                            <DialogTitle>
                                {editing ? `Edit · ${editing.model_name}` : "Add a new home"}
                            </DialogTitle>
                        </DialogHeader>
                        <HomeForm value={form} onChange={setForm} />
                        <DialogFooter className="gap-2">
                            <Button variant="outline" onClick={() => setOpenDialog(false)}>
                                Cancel
                            </Button>
                            <Button
                                onClick={submit}
                                className="bg-[#0F2A47] hover:bg-[#14385E] text-white"
                                data-testid="catalog-form-submit-button"
                            >
                                {editing ? "Save changes" : "Add home"}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>

            <div className="flex flex-wrap gap-3 items-center">
                <div className="relative flex-1 min-w-[240px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                        placeholder="Search by manufacturer, series, or model"
                        className="pl-9"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        data-testid="catalog-search-input"
                    />
                </div>
                <Select value={manufacturerFilter} onValueChange={setManufacturerFilter}>
                    <SelectTrigger className="w-[220px]" data-testid="catalog-manufacturer-filter">
                        <SelectValue placeholder="All manufacturers" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="__all__">All manufacturers</SelectItem>
                        {manufacturers.map((m) => (
                            <SelectItem key={m} value={m}>
                                {m}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <label className="flex items-center gap-2 text-sm text-slate-700">
                    <Switch checked={showInactive} onCheckedChange={setShowInactive} data-testid="catalog-show-inactive-switch" />
                    Show inactive
                </label>
            </div>

            <div className="overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Manufacturer</TableHead>
                            <TableHead>Series / Model</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead className="text-right">Beds / Baths</TableHead>
                            <TableHead className="text-right">Sqft</TableHead>
                            <TableHead>Media</TableHead>
                            <TableHead className="text-right">Base price</TableHead>
                            <TableHead>Catalog completeness</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={10} className="text-center text-slate-500 py-6">
                                    Loading...
                                </TableCell>
                            </TableRow>
                        ) : filtered.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={10} className="text-center text-slate-500 py-6">
                                    No homes match the current filters.
                                </TableCell>
                            </TableRow>
                        ) : (
                            filtered.map((h) => (
                                <TableRow
                                    key={h.id}
                                    className={!h.active ? "bg-slate-50/60 text-slate-500" : ""}
                                    data-testid={`catalog-row-${h.id}`}
                                >
                                    <TableCell className="text-sm">{h.manufacturer}</TableCell>
                                    <TableCell className="text-sm">
                                        <div className="font-medium text-slate-900">{h.model_name}</div>
                                        <div className="text-xs text-slate-500">{h.series || "-"}</div>
                                    </TableCell>
                                    <TableCell className="text-sm">{h.home_type || "-"}</TableCell>
                                    <TableCell className="text-right tabular text-sm">
                                        {h.beds || "-"} / {h.baths || "-"}
                                    </TableCell>
                                    <TableCell className="text-right tabular text-sm">{h.sqft || "-"}</TableCell>
                                    <TableCell>
                                        <div className="flex flex-wrap gap-1">
                                            <Badge variant="outline" className={hasHomePhoto(h) ? "border-emerald-200 text-emerald-700" : "border-slate-200 text-slate-500"}>
                                                Photo
                                            </Badge>
                                            <Badge variant="outline" className={hasFloorplan(h) ? "border-emerald-200 text-emerald-700" : "border-slate-200 text-slate-500"}>
                                                Plan
                                            </Badge>
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-right tabular text-sm font-medium">
                                        {money(h.ehs_price || 0)}
                                    </TableCell>
                                    <TableCell>
                                        {(() => {
                                            const completeness = getHomeCatalogCompleteness(h);
                                            const badgeClass = completeness.severity === "complete"
                                                ? "bg-emerald-100 text-emerald-800 border-emerald-200"
                                                : completeness.severity === "attention"
                                                    ? "bg-amber-100 text-amber-800 border-amber-200"
                                                    : "bg-rose-100 text-rose-800 border-rose-200";
                                            return (
                                                <Badge variant="outline" className={badgeClass}>
                                                    {completeness.label}
                                                </Badge>
                                            );
                                        })()}
                                    </TableCell>
                                    <TableCell>
                                        {h.active ? (
                                            <Badge className="bg-[rgba(67,161,215,0.12)] text-[#1E6FA8] border-[rgba(67,161,215,0.3)] hover:bg-[rgba(67,161,215,0.16)]">
                                                Active
                                            </Badge>
                                        ) : (
                                            <Badge variant="outline" className="text-slate-500 border-slate-200">
                                                Inactive
                                            </Badge>
                                        )}
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <div className="inline-flex gap-1">
                                            <Button
                                                size="icon"
                                                variant="ghost"
                                                onClick={() => startEdit(h)}
                                                title="Edit"
                                                data-testid={`catalog-edit-${h.id}`}
                                            >
                                                <Pencil className="h-4 w-4" />
                                            </Button>
                                            <Button
                                                size="icon"
                                                variant="ghost"
                                                onClick={() => toggleActive(h)}
                                                title={h.active ? "Deactivate" : "Reactivate"}
                                                data-testid={`catalog-toggle-${h.id}`}
                                            >
                                                {h.active ? <Power className="h-4 w-4" /> : <RotateCcw className="h-4 w-4" />}
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
                <div className="mt-2 text-xs text-slate-500">
                    Showing {filtered.length} of {homes.length} loaded homes.
                </div>
            </div>
        </Card>
    );
}
